export const onlyDigits = (value: string | number): string => String(value).replace(/\D/g, '');

export const onlyNumbers = (value: string | number): string => {
  const cleaned = String(value)
    .replace(/,/g, '.')
    .replace(/[^0-9.]/g, '')
    .replace(/(\..*)\./, '$1');

  if (!cleaned) return '';

  const [intPart, decPart] = cleaned.split('.');
  const integer = intPart.replace(/^0+(?=\d)/, '');

  return decPart != null ? `${integer || '0'}.${decPart.slice(0, 2)}` : integer || '0';
};

export const onlyNumbersWithoutDigits = (value: string | number): string => {
  const digits = String(value).replace(/\D/g, '');

  if (!digits) return '';

  return digits.replace(/^0+(?=\d)/, '') || '0';
};

export const onlyPhoneNumber = (value: string | number): string => {
  return String(value).replace(/[^0-9+-]/g, '');
};

export const formatNumberInputValue = ({
  value,
  limit,
  decimalLimit,
}: {
  value: string;
  limit?: number;
  decimalLimit?: number;
}) => {
  // Remove non-numeric characters and format the value
  const sanitizedValue = onlyNumbers(value);

  // Convert to an array for easier manipulation
  const valueArray = sanitizedValue.split('');

  // Handle leading zeros
  if (valueArray.length > 1 && valueArray[0] === '0' && valueArray[1] !== '.') {
    valueArray.splice(0, 1);
  }

  // Handle values that start with a decimal point
  if (valueArray.length === 1 && valueArray[0] === '.') {
    valueArray.splice(0, 0, '0');
  }

  // Convert back to a string after formatting
  let editedValue = valueArray.join('');

  // Check if the value exceeds the limit
  if (limit !== undefined && parseFloat(editedValue) > limit) {
    // If the value exceeds the limit, return the previous value up to the limit
    editedValue = limit.toString();
  }
  if (decimalLimit !== undefined) {
    const [integerPart, decimalPart] = editedValue.split('.');

    // If there's a decimal part and it exceeds the limit, truncate it
    if (decimalPart && decimalPart.length > decimalLimit) {
      editedValue = `${integerPart}.${decimalPart.slice(0, decimalLimit)}`;
    }
  }

  return editedValue;
};

/**
 * Parses a user-entered USD amount into a number.
 * Accepts plain digits, thousands separators, a currency prefix and the k/M/B
 * shorthands: "2500000", "2,500,000", "$2.5M", "750k", "1.2B". Returns null when
 * the input is not a positive amount, so callers can reject it.
 *
 * The shorthands mirror the units amounts are displayed in, so any value the UI
 * abbreviates can be typed back in the form it was read.
 */
export const parseUsdAmount = (raw: string): number | null => {
  const match = raw
    .trim()
    .replace(/\$/g, '')
    .match(/^([\d,]*\.?\d+)\s*([bmk])?$/i);
  if (!match) return null;

  let value = Number(match[1].replace(/,/g, ''));
  const suffix = match[2]?.toLowerCase();
  if (suffix === 'b') value *= 1_000_000_000;
  if (suffix === 'm') value *= 1_000_000;
  if (suffix === 'k') value *= 1_000;

  return Number.isFinite(value) && value > 0 ? value : null;
};

export const matchIsNumeric = (text: string): boolean => {
  const isNumber = typeof text === 'number';

  const normalized = typeof text === 'string' ? text.replace(/,/g, '.') : text;
  return (isNumber || text !== '') && !isNaN(Number(normalized));
};
